<template>
  <div id="printView">
    <v-card v-for="releaseNote in releaseNoteList" :key="releaseNote.id" class="elevation-0" data-cy="releaseNote">
      <v-card-title :data-cy="`releaseNotePrintTitle-${releaseNote[idAttribute]}`" class="nowrap">{{
        releaseNoteTitle(releaseNote)
      }}</v-card-title>
      <v-card-text>
        <div
          v-if="releaseNote.packTitle"
          :data-cy="`releaseNotePrintPackTitle-${releaseNote[idAttribute]}`"
          class="text-block"
        >
          <span class="font-weight-bold">Pack: </span>{{ releaseNote.packTitle }}
        </div>

        <div
          v-if="releaseNote.updatedReason"
          :data-cy="`releaseNotePrintUpdatedReason-${releaseNote[idAttribute]}`"
          data-test="releaseNotePrintUpdatedReason"
          class="text-block"
        >
          <span class="font-weight-bold"> Update: </span>
          <embedded-html :value="releaseNote.updatedReason"></embedded-html>
        </div>

        <div
          v-if="releaseNote.summary"
          :data-cy="`releaseNotePrintSummary-${releaseNote[idAttribute]}`"
          data-test="releaseNotePrintSummary"
          class="text-block"
        >
          <span class="font-weight-bold">Summary: </span>
          <embedded-html :value="releaseNote.summary"></embedded-html>
        </div>

        <div class="px-4">
          <v-row dense>
            <v-col
              v-if="releaseNote.application"
              :data-cy="`releaseNotePrintModule-${releaseNote[idAttribute]}`"
              data-test="releaseNotePrintModule"
              class="col"
            >
              <div><span class="font-weight-bold"> Application:</span> {{ releaseNote.application }}</div>
            </v-col>
            <v-col
              v-if="releaseNote.operationalizeType"
              :data-cy="`releaseNotePrintOperationalizeType-${releaseNote[idAttribute]}`"
              data-test="releaseNotePrintOperationalizeType"
              class="col"
            >
              <div>
                <span class="font-weight-bold"> Operationalize Type:</span> {{ releaseNote.operationalizeType }}
              </div>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col
              v-if="releaseNote.primaryActivity"
              :data-cy="`releaseNotePrintPrimaryActivity-${releaseNote[idAttribute]}`"
              data-test="releaseNotePrintPrimaryActivity"
              class="col"
            >
              <div><span class="font-weight-bold">Primary Activity:</span> {{ releaseNote.primaryActivity }}</div>
            </v-col>
            <v-col
              v-if="releaseNote.releaseType"
              :data-cy="`releaseNotePrintReleaseType-${releaseNote[idAttribute]}`"
              data-test="releaseNotePrintReleaseType"
              class="col"
            >
              <div><span class="font-weight-bold">Release Type:</span> {{ releaseNote.releaseType }}</div>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col
              v-if="releaseNote.activitiesImpacted"
              :data-cy="`releaseNotePrintOtherActivities-${releaseNote[idAttribute]}`"
              data-test="releaseNotePrintOtherActivities"
              class="col"
            >
              <div>
                <span class="font-weight-bold"> Other Activities Impacted:</span>
                {{ releaseNote.activitiesImpacted }}
              </div>
            </v-col>
            <v-col
              v-if="releaseNote.buildType"
              :data-cy="`releaseNotePrintBuildType-${releaseNote[idAttribute]}`"
              data-test="releaseNotePrintBuildType"
              class="col"
            >
              <div>
                <span class="font-weight-bold"> Build Type:</span>
                {{ releaseNote.buildType }}
              </div>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col
              v-if="releaseNote.userRole"
              :data-cy="`releaseNotePrintUserRole-${releaseNote[idAttribute]}`"
              data-test="releaseNotePrintUserRole"
              class="col"
            >
              <div>
                <span class="font-weight-bold"> User Role:</span>
                {{ formatArray(releaseNote.userRole) }}
              </div>
            </v-col>
            <v-col
              v-if="releaseNote.uatJira"
              :data-cy="`releaseNotePrintUatJira-${releaseNote[idAttribute]}`"
              data-test="releaseNotePrintUatJira"
              class="col"
            >
              <div>
                <span class="font-weight-bold"> UAT Jira:</span>
                {{ releaseNote.uatJira }}
              </div>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col
              v-if="releaseNote.careSetting"
              :data-cy="`releaseNotePrintCareSetting-${releaseNote[idAttribute]}`"
              data-test="releaseNotePrintCareSetting"
              class="col"
            >
              <div>
                <span class="font-weight-bold"> Care Setting:</span>
                {{ releaseNote.careSetting }}
              </div>
            </v-col>
          </v-row>
        </div>
        <div
          v-if="releaseNote.preGoLiveSetup"
          :data-cy="`releaseNotePrintPreGoLiveSetup-${releaseNote[idAttribute]}`"
          data-test="releaseNotePrintPreGoLiveSetup"
          class="text-block"
        >
          <span class="font-weight-bold">Pre Go Live Setup:</span>
          <embedded-html :value="releaseNote.preGoLiveSetup"></embedded-html>
        </div>
        <div
          v-if="releaseNote.background"
          :data-cy="`releaseNotePrintBackground-${releaseNote[idAttribute]}`"
          data-test="releaseNotePrintBackground"
          class="text-block"
        >
          <span class="font-weight-bold">Background:</span>
          <embedded-html :value="releaseNote.background"></embedded-html>
        </div>
        <div v-if="releaseNote.issue" :data-cy="`releaseNotePrintIssue-${releaseNote[idAttribute]}`" class="text-block">
          <div><span class="font-weight-bold">Issue:</span> {{ releaseNote.issue }}</div>
        </div>
        <div
          v-if="releaseNote.currentBehavior"
          :data-cy="`releaseNotePrintCurrentBehavior-${releaseNote[idAttribute]}`"
          data-test="releaseNotePrintCurrentBehavior"
          class="text-block"
        >
          <span class="font-weight-bold">Current Behavior:</span>
          <embedded-html :value="releaseNote.currentBehavior"></embedded-html>
        </div>
        <div
          v-if="releaseNote.newBehavior"
          :data-cy="`releaseNotePrintNewBehavior-${releaseNote[idAttribute]}`"
          data-test="releaseNotePrintNewBehavior"
          class="text-block"
        >
          <span class="font-weight-bold">New Behavior:</span>
          <embedded-html :value="releaseNote.newBehavior"> </embedded-html>
        </div>
        <div
          v-if="releaseNote.optionalEnhancement"
          :data-cy="`releaseNotePrintOptionalEnhancement-${releaseNote[idAttribute]}`"
          data-test="releaseNotePrintOptionalEnhancement"
          class="text-block"
        >
          <span class="font-weight-bold">Optional Enhancement:</span>
          <embedded-html :value="releaseNote.optionalEnhancement"> </embedded-html>
        </div>
        <div
          v-if="releaseNote.note"
          :data-cy="`releaseNotePrintNote-${releaseNote[idAttribute]}`"
          data-test="releaseNotePrintNote"
          class="text-block"
        >
          <span class="font-weight-bold">Note:</span>
          <embedded-html :value="releaseNote.note"> </embedded-html>
        </div>
        <div class="px-4">
          <v-row dense>
            <v-col
              v-if="releaseNote.buildAnalyst"
              :data-cy="`releaseNotePrintBuildAnalyst-${releaseNote[idAttribute]}`"
              data-test="releaseNotePrintBuildAnalyst"
              class="col"
            >
              <div>
                <span class="font-weight-bold"> Build Analyst:</span> {{ releaseNote.buildAnalystDisplayName || "--" }}
              </div>
            </v-col>
            <v-col
              v-if="releaseNote.qaAnalyst"
              :data-cy="`releaseNotePrintQaAnalyst-${releaseNote[idAttribute]}`"
              data-test="releaseNotePrintQaAnalyst"
              class="col"
            >
              <div>
                <span class="font-weight-bold">QA Analyst:</span> {{ releaseNote.qaAnalystDisplayName || "--" }}
              </div>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col
              v-if="releaseNote.buildJira && isOchinUser"
              :data-cy="`releaseNotePrintBuildJira-${releaseNote[idAttribute]}`"
              data-test="releaseNotePrintBuildJira"
              class="col"
            >
              <div>
                <span class="font-weight-bold">Build Jira: </span>
                <span v-for="(jira, index) in splitBuildJiras(releaseNote.buildJira)" :key="index">
                  <span v-if="index > 0">, </span>
                  <template v-if="isJiraKey(jira)">
                    <a :href="`https://jira.ochin.org/browse/${jira}`" target="_blank" rel="noopener noreferrer">{{ jira }}</a>
                  </template>
                  <template v-else>{{ jira }}</template>
                </span>
              </div>
            </v-col>
            <v-col v-if="releaseNote.qaJira && isOchinUser" class="col">
              <div :data-cy="`releaseNotePrintQaJira-${releaseNote[idAttribute]}`" data-test="releaseNotePrintQaJira">
                <span class="font-weight-bold">QA JIRA:</span> {{ releaseNote.qaJira }}
              </div>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col
              v-if="releaseNote.jiraSupportAnalyst"
              :data-cy="`releaseNotePrintReportAnalyst-${releaseNote[idAttribute]}`"
              data-test="releaseNotePrintReportAnalyst"
              class="col"
            >
              <div>
                <span class="font-weight-bold"> JIRA Support Analyst:</span>
                <ul v-for="jiraSupportAnalyst in releaseNote.jiraSupportAnalyst">
                  <li v-text="formatUsername(jiraSupportAnalyst)"></li>
                </ul>
              </div>
            </v-col>
            <v-col
              v-if="releaseNote.trainingJira && isOchinUser"
              :data-cy="`releaseNotePrintTrainingJira-${releaseNote[idAttribute]}`"
              data-test="releaseNotePrintTrainingJira"
              class="col"
            >
              <div>
                <span class="font-weight-bold">Training Jira: </span>
                <template v-if="isJiraKey(releaseNote.trainingJira)">
                  <a :href="`https://jira.ochin.org/browse/${releaseNote.trainingJira}`" target="_blank" rel="noopener noreferrer">{{ releaseNote.trainingJira }}</a>
                </template>
                <template v-else>{{ releaseNote.trainingJira }}</template>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
      <p class="page_break"></p>
    </v-card>
  </div>
</template>

<script>
import EmbeddedHtml from "@/components/embeddedHtml";
import { formatArray, formatUsername, isJiraKey } from "@/lib/formatters";

import { mapActions, mapGetters, mapState } from "vuex";

export default {
  name: "print",
  components: {
    EmbeddedHtml,
  },
  computed: {
    idAttribute() {
      if (this.user.isOchinUser) {
        return "id";
      }
      return "releaseNoteId";
    },
    isOchinUser() {
      return this.$store.state.session.user.isOchinUser || false;
    },
    ...mapGetters({
      userMap: "user/getOchinUserList",
    }),
    ...mapState({
      user: state => state.session.user,
    }),
  },
  data() {
    return {
      formatArray,
    };
  },
  async created() {
    await this.fetchUserList();
  },
  methods: {
    formatUsername(username) {
      return formatUsername(username, this.userMap);
    },
    isJiraKey,
    splitBuildJiras(buildJira) {
      return buildJira ? buildJira.split(",").map(j => j.trim()).filter(Boolean) : [];
    },
    releaseNoteTitle(releaseNote) {
      if (this.mode === "releaseNote") {
        return `${releaseNote.title} [ID:${releaseNote[this.idAttribute]}]`;
      }
      return `${releaseNote.title}`;
    },
    ...mapActions({
      fetchUserList: "user/loadOchinUsers",
    }),
  },
  props: {
    mode: { required: true, type: String },
    releaseNoteList: { required: true, type: Array },
  },
};
</script>

<style scoped>
.col {
  padding: 0;
  margin-top: 0;
  margin-bottom: 0;
}

.nowrap {
  white-space: nowrap;
}

.page_break {
  page-break-after: always;
}

.text-block {
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
  break-inside: avoid-region;
  overflow: visible !important;
}

@media print {
  @page {
    margin-bottom: 1cm;
    margin-left: 1cm;
    margin-right: 1cm;
    margin-top: 1cm;
    size: 8.5in 11in;
  }
}
</style>
